/**
 * ConversationContextMenu Component
 * 
 * Context menu for conversation list items.
 * Shows on right-click (desktop) or long-press (mobile).
 * Options:
 * - Pin/Unpin conversation
 * - Mark as Read/Unread
 * - Archive conversation
 */

import React, { useRef, useEffect } from 'react';
import { Pin, PinOff, Mail, MailOpen, Archive } from 'lucide-react';
import { useDesign } from '@/contexts/DesignContext';

export default function ConversationContextMenu({
  isOpen,
  position,
  isPinned,
  hasUnread,
  onPin,
  onUnpin,
  onMarkRead,
  onMarkUnread,
  onArchive,
  onClose
}) {
  const { isStudio } = useDesign();
  const menuRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };

    // Small delay to prevent immediate close
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }, 50);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Close on escape
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handlePinClick = () => {
    if (isPinned) {
      onUnpin();
    } else {
      onPin();
    }
    onClose();
  };

  const handleReadClick = () => {
    if (hasUnread) {
      onMarkRead();
    } else {
      onMarkUnread();
    }
    onClose();
  };

  const handleArchiveClick = () => {
    if (onArchive) {
      onArchive();
    }
    onClose();
  };

  // Calculate position to ensure menu stays within viewport
  const menuStyle = {
    position: 'fixed',
    top: position.y,
    left: position.x,
    zIndex: 1000
  };

  return (
    <div
      ref={menuRef}
      data-ui="menu"
      className="bg-card rounded-xl shadow-xl border border-slate-200 dark:border-white/10 py-1 min-w-[180px] animate-in fade-in zoom-in-95 duration-150"
      style={menuStyle}
      data-testid="conversation-context-menu"
    >
      {/* Pin/Unpin option */}
      <button
        onClick={handlePinClick}
        data-ui="mi"
        className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
        data-testid="context-menu-pin"
      >
        {isPinned ? (
          <>
            <PinOff className="w-4 h-4 text-slate-500 dark:text-slate-400" />
            <span>Unpin</span>
          </>
        ) : (
          <>
            <Pin className="w-4 h-4 text-slate-500 dark:text-slate-400" />
            <span>Pin</span>
          </>
        )}
      </button>

      {/* Divider */}
      <div className="h-px bg-slate-100 dark:bg-[#3a3d41] my-1" />

      {/* Mark Read/Unread option */}
      <button
        onClick={handleReadClick}
        data-ui="mi"
        className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
        data-testid="context-menu-read"
      >
        {hasUnread ? (
          <>
            <MailOpen className="w-4 h-4 text-slate-500 dark:text-slate-400" />
            <span>{isStudio ? 'Mark as read' : 'Mark as Read'}</span>
          </>
        ) : (
          <>
            <Mail className="w-4 h-4 text-slate-500 dark:text-slate-400" />
            <span>{isStudio ? 'Mark as unread' : 'Mark as Unread'}</span>
          </>
        )}
      </button>

      {/* Divider */}
      <div className="h-px bg-slate-100 dark:bg-[#3a3d41] my-1" />

      {/* Archive option */}
      <button
        onClick={handleArchiveClick}
        data-ui="mi"
        className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
        data-testid="context-menu-archive"
      >
        <Archive className="w-4 h-4 text-slate-500 dark:text-slate-400" />
        <span>Archive</span>
      </button>
    </div>
  );
}
